import mermaid from 'mermaid';
import { useEffect, useRef, useState } from 'react';
import {
  BubbleProps,
  ChartBlock,
  exportCSV,
  FlowBlock,
  formatTime,
  Message,
  SUGGESTED_PROMPTS,
  TableBlock,
  TextBlock,
  TOOL_LABELS,
  uid,
  XET_THEME,
} from './BoxChatModel';

export const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M14 8L2 2l2.5 6L2 14l12-6z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

export const BoxIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M10 2L2 6v8l8 4 8-4V6L10 2z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
    <path
      d="M2 6l8 4 8-4M10 10v10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

export const SpinnerIcon = ({
  color = XET_THEME.yellow,
}: {
  color?: string;
}) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    style={{ animation: 'xspin 0.8s linear infinite', flexShrink: 0 }}
  >
    <circle
      cx="7"
      cy="7"
      r="5.5"
      stroke={color}
      strokeWidth="1.5"
      strokeOpacity="0.25"
    />
    <path
      d="M7 1.5A5.5 5.5 0 0112.5 7"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export const DownloadIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path
      d="M6.5 1v7M4 6l2.5 2.5L9 6"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1 10h11"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

export const XetgoLogo = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <rect width="28" height="28" rx="6" fill={XET_THEME.white} />
    <path
      d="M7 7L14 14M21 7L14 14M14 14L7 21M14 14L21 21"
      stroke={XET_THEME.navy}
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    <path
      d="M18 8L22 4"
      stroke={XET_THEME.yellow}
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>
);

// ─── BLOCK RENDERERS ──────────────────────────────────────────────────────────

export function TextRenderer({
  block,
  isStreaming,
}: {
  block: TextBlock;
  isStreaming?: boolean;
}) {
  return (
    <div
      style={{
        padding: '12px 16px',
        borderRadius: '4px 12px 12px 12px',
        background: XET_THEME.white,
        border: `1px solid ${XET_THEME.gray100}`,
        color: XET_THEME.text,
        fontSize: 14,
        lineHeight: 1.7,
        fontFamily: "'Poppins', sans-serif",
        whiteSpace: 'pre-wrap',
        boxShadow: '0 1px 4px rgba(13,27,75,0.06)',
      }}
    >
      {block.content}
      {isStreaming && (
        <span
          style={{
            display: 'inline-block',
            width: 2,
            height: '1em',
            background: XET_THEME.navy,
            marginLeft: 2,
            verticalAlign: 'text-bottom',
            animation: 'xblink 0.8s ease-in-out infinite',
          }}
        />
      )}
    </div>
  );
}

export function TableRenderer({ block }: { block: TableBlock }) {
  return (
    <div
      style={{
        background: XET_THEME.white,
        border: `1px solid ${XET_THEME.gray100}`,
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(13,27,75,0.06)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 16px',
          background: XET_THEME.navy,
        }}
      >
        <div>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: XET_THEME.white,
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            {block.title ?? 'Table'}
          </span>
          <span
            style={{
              marginLeft: 8,
              fontSize: 11,
              color: 'rgba(255,255,255,0.5)',
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            {block.rows.length} rows
          </span>
        </div>
        <button
          onClick={() => exportCSV(block)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 11,
            fontFamily: "'Poppins', sans-serif",
            padding: '5px 10px',
            borderRadius: 6,
            background: XET_THEME.yellow,
            border: 'none',
            color: XET_THEME.navy,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          <DownloadIcon /> Export CSV
        </button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 12,
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          <thead>
            <tr style={{ background: XET_THEME.gray50 }}>
              {block.headers.map((h, i) => (
                <th
                  key={i}
                  style={{
                    padding: '9px 14px',
                    textAlign: 'left',
                    color: XET_THEME.navyMid,
                    fontWeight: 600,
                    borderBottom: `2px solid ${XET_THEME.gray100}`,
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.02em',
                    fontSize: 11,
                    textTransform: 'uppercase',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, ri) => (
              <tr
                key={ri}
                style={{
                  background:
                    ri % 2 === 0 ? XET_THEME.white : XET_THEME.offWhite,
                  transition: 'background 0.1s',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = XET_THEME.gray50)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background =
                    ri % 2 === 0 ? XET_THEME.white : XET_THEME.offWhite)
                }
              >
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    style={{
                      padding: '8px 14px',
                      color: XET_THEME.text,
                      borderBottom: `1px solid ${XET_THEME.gray100}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ChartRenderer({ block }: { block: ChartBlock }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const W = canvas.offsetWidth,
      H = canvas.offsetHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);

    const PAD = { top: 28, right: 20, bottom: 56, left: 60 };
    const cW = W - PAD.left - PAD.right;
    const cH = H - PAD.top - PAD.bottom;
    const all = block.datasets.flatMap((d) => d.data);
    const maxVal = Math.max(...all, 1);
    const COLORS = [
      XET_THEME.yellow,
      XET_THEME.navyMid,
      '#E53E3E',
      '#2F855A',
      '#805AD5',
    ];
    const n = block.labels.length;

    // Grid
    for (let i = 0; i <= 4; i++) {
      const y = PAD.top + cH - (i / 4) * cH;
      ctx.strokeStyle = XET_THEME.gray100;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(PAD.left, y);
      ctx.lineTo(PAD.left + cW, y);
      ctx.stroke();
      ctx.fillStyle = XET_THEME.gray500;
      ctx.font = `10px 'Poppins', sans-serif`;
      ctx.textAlign = 'right';
      ctx.fillText(String(Math.round((i / 4) * maxVal)), PAD.left - 8, y + 3);
    }

    if (block.chartType === 'bar') {
      const groupW = cW / n;
      const dsCount = block.datasets.length;
      const barW = Math.max(6, (groupW - 8 * (dsCount + 1)) / dsCount);
      block.datasets.forEach((ds, di) => {
        ctx.fillStyle = COLORS[di % COLORS.length];
        ds.data.forEach((val, li) => {
          const bH = (val / maxVal) * cH;
          const x = PAD.left + li * groupW + 8 * (di + 1) + di * barW;
          const y = PAD.top + cH - bH;
          ctx.beginPath();
          if (ctx.roundRect) {
            ctx.roundRect(x, y, barW, bH, [3, 3, 0, 0]);
          } else {
            ctx.rect(x, y, barW, bH); // fallback
          }
          ctx.fill();
        });
      });
    } else {
      block.datasets.forEach((ds, di) => {
        ctx.strokeStyle = COLORS[di % COLORS.length];
        ctx.lineWidth = 2.5;
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ds.data.forEach((val, li) => {
          const x = PAD.left + (li + 0.5) * (cW / n);
          const y = PAD.top + cH - (val / maxVal) * cH;
          li === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        });
        ctx.stroke();
        ctx.fillStyle = COLORS[di % COLORS.length];
        ds.data.forEach((val, li) => {
          const x = PAD.left + (li + 0.5) * (cW / n);
          const y = PAD.top + cH - (val / maxVal) * cH;
          ctx.beginPath();
          ctx.arc(x, y, 4, 0, Math.PI * 2);
          ctx.fill();
        });
      });
    }

    ctx.fillStyle = XET_THEME.gray700;
    ctx.font = `10px 'Poppins', sans-serif`;
    ctx.textAlign = 'center';
    block.labels.forEach((label, li) => {
      const x = PAD.left + (li + 0.5) * (cW / n);
      ctx.fillText(
        label.length > 12 ? label.slice(0, 11) + '…' : label,
        x,
        PAD.top + cH + 18
      );
    });

    if (block.datasets.length > 1) {
      let lx = PAD.left;
      block.datasets.forEach((ds, di) => {
        ctx.fillStyle = COLORS[di % COLORS.length];
        ctx.fillRect(lx, PAD.top + cH + 32, 10, 10);
        ctx.fillStyle = XET_THEME.gray700;
        ctx.textAlign = 'left';
        ctx.fillText(ds.label, lx + 14, PAD.top + cH + 41);
        lx += ctx.measureText(ds.label).width + 28;
      });
    }
  }, [block]);

  return (
    <div
      style={{
        background: XET_THEME.white,
        border: `1px solid ${XET_THEME.gray100}`,
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(13,27,75,0.06)',
      }}
    >
      {block.title && (
        <div
          style={{
            padding: '10px 16px',
            background: XET_THEME.navy,
            fontSize: 12,
            fontWeight: 600,
            color: XET_THEME.white,
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          {block.title}
        </div>
      )}
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: 240, display: 'block' }}
      />
    </div>
  );
}

export function FlowchartRenderer({ block }: { block: FlowBlock }) {
  const ref = useRef<HTMLDivElement>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function render() {
      try {
        if (cancelled) return;
        mermaid.initialize({
          startOnLoad: false,
          theme: 'base',
          themeVariables: {
            primaryColor: '#E8EDFF',
            primaryTextColor: XET_THEME.navy,
            primaryBorderColor: XET_THEME.navyBorder,
            lineColor: XET_THEME.navyMid,
            secondaryColor: XET_THEME.gray50,
            fontSize: '12px',
          },
        });
        const { svg } = await mermaid.render(`m-${uid()}`, block.mermaid);
        if (!cancelled && ref.current) ref.current.innerHTML = svg;
      } catch {
        if (!cancelled) setErr('Could not render flowchart.');
      }
    }
    render();
    return () => {
      cancelled = true;
    };
  }, [block.mermaid]);

  return (
    <div
      style={{
        background: XET_THEME.white,
        border: `1px solid ${XET_THEME.gray100}`,
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(13,27,75,0.06)',
      }}
    >
      {block.title && (
        <div
          style={{
            padding: '10px 16px',
            background: XET_THEME.navy,
            fontSize: 12,
            fontWeight: 600,
            color: XET_THEME.white,
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          {block.title}
        </div>
      )}
      {err ? (
        <div
          style={{
            padding: 16,
            fontSize: 11,
            color: XET_THEME.danger,
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          {err}
        </div>
      ) : (
        <div ref={ref} style={{ padding: '12px 16px', overflowX: 'auto' }} />
      )}
    </div>
  );
}

// ─── TOOL PILL ────────────────────────────────────────────────────────────────

export function ToolPill({ tools }: { tools: string[] }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '6px 12px',
        borderRadius: 20,
        background: 'rgba(251,188,4,0.12)',
        border: `1px solid rgba(251,188,4,0.3)`,
        fontSize: 11,
        fontFamily: "'Poppins', sans-serif",
        color: XET_THEME.yellow,
      }}
    >
      <SpinnerIcon color={XET_THEME.yellow} />
      {tools.map((t) => TOOL_LABELS[t] ?? t).join(' · ')}…
    </div>
  );
}

// ─── MESSAGE BUBBLE ───────────────────────────────────────────────────────────

export function MessageBubble({
  msg,
  isStreaming,
}: {
  msg: Message;
  isStreaming?: boolean;
}) {
  const isUser = msg.role === 'user';

  if (isUser) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 10,
          alignItems: 'flex-start',
          animation: 'xfade 0.2s ease forwards',
        }}
      >
        <div
          style={{
            maxWidth: '72%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 4,
          }}
        >
          <div
            style={{
              padding: '11px 16px',
              borderRadius: '12px 4px 12px 12px',
              background: `linear-gradient(135deg, ${XET_THEME.navyMid}, ${XET_THEME.navy})`,
              color: XET_THEME.white,
              fontSize: 14,
              lineHeight: 1.65,
              fontFamily: "'Poppins', sans-serif",
              whiteSpace: 'pre-wrap',
              boxShadow: '0 2px 12px rgba(13,27,75,0.2)',
            }}
          >
            {msg.rawText}
          </div>
          <span
            style={{
              fontSize: 10,
              color: XET_THEME.gray500,
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            {formatTime(msg.ts)}
          </span>
        </div>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            flexShrink: 0,
            background: XET_THEME.gray100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
            color: XET_THEME.navyMid,
            fontFamily: "'Poppins', sans-serif",
            marginTop: 2,
          }}
        >
          U
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        animation: 'xfade 0.2s ease forwards',
      }}
    >
      {/* Xetgo avatar */}
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          flexShrink: 0,
          background: XET_THEME.navy,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 2,
          flexDirection: 'column',
        }}
      >
        <XetgoLogo />
      </div>

      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {/* Tool badges */}
        {msg.toolsUsed && msg.toolsUsed.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {msg.toolsUsed.map((t) => (
              <span
                key={t}
                style={{
                  fontSize: 10,
                  fontFamily: "'Poppins', sans-serif",
                  padding: '2px 8px',
                  borderRadius: 4,
                  background: 'rgba(47,133,90,0.1)',
                  border: '1px solid rgba(47,133,90,0.25)',
                  color: XET_THEME.success,
                  letterSpacing: '0.02em',
                  fontWeight: 500,
                }}
              >
                ✓ {TOOL_LABELS[t] ?? t}
              </span>
            ))}
          </div>
        )}

        {msg.blocks.map((block, i) => {
          const last = i === msg.blocks.length - 1;
          if (block.type === 'text')
            return (
              <TextRenderer
                key={i}
                block={block}
                isStreaming={isStreaming && last}
              />
            );
          if (block.type === 'table')
            return <TableRenderer key={i} block={block} />;
          if (block.type === 'chart')
            return <ChartRenderer key={i} block={block} />;
          if (block.type === 'flowchart')
            return <FlowchartRenderer key={i} block={block} />;
          return null;
        })}

        <span
          style={{
            fontSize: 10,
            color: XET_THEME.gray500,
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          {formatTime(msg.ts)}
        </span>
      </div>
    </div>
  );
}

// ─── EMPTY STATE ──────────────────────────────────────────────────────────────

export function EmptyState({ onSelect }: { onSelect: (p: string) => void }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: 36,
        padding: '40px 24px',
      }}
    >
      {/* Hero mark */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: 18,
            background: XET_THEME.navy,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 32px rgba(13,27,75,0.25)',
            position: 'relative',
          }}
        >
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <rect
              width="40"
              height="40"
              rx="10"
              fill={XET_THEME.white}
              fillOpacity="0.1"
            />
            <path
              d="M10 10L20 20M30 10L20 20M20 20L10 30M20 20L30 30"
              stroke={XET_THEME.white}
              strokeWidth="3"
              strokeLinecap="round"
            />
            <path
              d="M26 11L32 5"
              stroke={XET_THEME.yellow}
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
          {/* Pulse ring */}
          <div
            style={{
              position: 'absolute',
              inset: -6,
              borderRadius: 24,
              border: `2px solid ${XET_THEME.yellow}`,
              opacity: 0.3,
              animation: 'xpulse 2s ease-in-out infinite',
            }}
          />
        </div>

        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              fontSize: 26,
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 700,
              color: XET_THEME.navy,
              letterSpacing: '-0.02em',
            }}
          >
            Xetgo <span style={{ color: XET_THEME.yellow }}>AI</span> Assistant
          </div>
          <div
            style={{
              fontSize: 13,
              color: XET_THEME.gray500,
              fontFamily: "'Poppins', sans-serif",
              marginTop: 6,
            }}
          >
            Ask anything about orders, vendors, pricing & deliveries
          </div>
        </div>
      </div>

      {/* Suggested prompts */}
      <div
        style={{
          width: '100%',
          maxWidth: 580,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontFamily: "'Poppins', sans-serif",
            color: XET_THEME.gray300,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: 4,
            fontWeight: 600,
          }}
        >
          Suggested queries
        </div>
        {SUGGESTED_PROMPTS.map((prompt, i) => (
          <button
            key={i}
            onClick={() => onSelect(prompt)}
            style={{
              textAlign: 'left',
              padding: '11px 16px',
              background: XET_THEME.white,
              border: `1.5px solid ${XET_THEME.gray100}`,
              borderRadius: 10,
              cursor: 'pointer',
              fontSize: 13,
              fontFamily: "'Poppins', sans-serif",
              color: XET_THEME.text,
              lineHeight: 1.5,
              transition: 'all 0.15s',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = XET_THEME.yellow;
              e.currentTarget.style.background = '#FFFBEE';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = XET_THEME.gray100;
              e.currentTarget.style.background = XET_THEME.white;
            }}
          >
            <span
              style={{ color: XET_THEME.yellow, fontSize: 16, flexShrink: 0 }}
            >
              ›
            </span>
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
}
